/**
 * PG사 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Row, Col, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { pgService } from '../../services/pgService';
import { PgProvider } from '../../types/pg';
import { format } from 'date-fns';

const PgProviderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const providerId = id ? parseInt(id, 10) : 0;
  const isNew = id === 'new';

  const { data: provider, isLoading } = useQuery({
    queryKey: ['pgProvider', providerId],
    queryFn: () => pgService.getProvider(providerId),
    enabled: !!providerId && providerId > 0 && !isNew,
  });

  const deleteMutation = useMutation({
    mutationFn: () => pgService.deleteProvider(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pgProviders'] });
      navigate('/admin/pg/providers');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const handleDelete = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteMutation.mutate();
    }
  };

  if (isNew) {
    navigate('/admin/pg/providers/new');
    return null;
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!provider) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>PG사를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/pg/providers')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>PG사 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/pg/providers')} className="me-2">
              목록으로
            </Button>
            <Button variant="info" onClick={() => navigate(`/admin/pg/providers/${providerId}/settlements`)} className="me-2">
              정산 내역
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/pg/providers/${providerId}/edit`)} className="me-2">
              수정
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              삭제
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h3>{provider.provider_name}</h3>
                  <div className="mt-2">
                    {getStatusBadge(provider.status)}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>PG사 코드:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>{provider.provider_code}</span>
                </div>

                <div className="mb-3">
                  <strong>수수료율:</strong> 
                  <span style={{ color: '#00BCD4', fontWeight: 'bold', marginLeft: '10px' }}>
                    {provider.commission_rate}%
                  </span>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <strong>등록일:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>
                    {format(new Date(provider.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>

                {provider.updated_at && (
                  <div className="mb-3">
                    <strong>수정일:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(provider.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">연동 정보</h5>
            {provider.api_key && (
              <div className="mb-3">
                <strong>API Key:</strong> 
                <div style={{ 
                  color: '#00BCD4', 
                  marginTop: '5px',
                  padding: '10px',
                  backgroundColor: '#2E2E3A',
                  borderRadius: '5px',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {provider.api_key}
                </div>
              </div>
            )}

            {provider.api_secret && (
              <div className="mb-3">
                <strong>API Secret:</strong> 
                <div style={{ 
                  color: '#00BCD4', 
                  marginTop: '5px',
                  padding: '10px',
                  backgroundColor: '#2E2E3A',
                  borderRadius: '5px',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {provider.api_secret}
                </div>
              </div>
            )}

            {!provider.api_key && !provider.api_secret && (
              <Alert variant="info">
                연동 정보가 설정되지 않았습니다. 수정 페이지에서 설정해주세요.
              </Alert>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>정산 현황</h5>
              <Button variant="info" size="sm" onClick={() => navigate(`/admin/pg/providers/${providerId}/settlements`)}>
                정산 내역 보기
              </Button>
            </div>
            <Alert variant="info">
              PG사별 정산 내역은 별도 페이지에서 확인할 수 있습니다.
            </Alert>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PgProviderDetail;

